import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Users, Crown, Search, Shield, ChevronDown, ChevronUp, X,
  Check, Loader2, History, ToggleLeft, ToggleRight, Save
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: "basique", name: "Basique", color: "#94a3b8", price: "Gratuit" },
  { id: "starter", name: "Starter", color: "#00B4D8", price: "25 000 FCFA/mois" },
  { id: "pro", name: "Pro", color: "#FFC300", price: "50 000 FCFA/mois" },
  { id: "complet", name: "Complet", color: "#10b981", price: "75 000 FCFA/mois" },
];

const FEATURE_LABELS = {
  import_excel_pdf: "Import Excel/PDF",
  destruction_express: "Destruction Express",
  certificats_pdf: "Certificats PDF",
  export_pdf: "Export PDF",
  export_excel: "Export Excel",
  recommendations: "Recommandations",
  itpp_sentinel: "ITPP/Sentinel",
  benchmark: "Benchmark",
  pharmexchange: "PharmExchange",
  alerts_sms_wa: "Alertes SMS/WhatsApp",
  monthly_reports: "Rapports mensuels",
  api_pos: "API Caisse",
  ai_assistant: "Assistant IA",
  qr_mobile_declaration: "QR Déclaration Mobile",
  ai_reports: "Rapports IA",
  ai_dashboard: "Tableau de bord IA",
  messagerie_assureurs: "Messagerie Assureurs",
  smart_alerts: "Alertes Intelligentes",
  check_scanning: "Scan Chèques",
  delivery_scanning: "Scan Bordereaux",
  expiry_alerts: "Alertes Peremption",
  tonnage_analytics: "Analytics Tonnage",
  ia_predictive: "IA Prédictive",
};

export default function SubscriptionManagementPage() {
  const { token } = useAuth();
  const [pharmacies, setPharmacies] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [overrideModal, setOverrideModal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        axios.get(`${API}/api/superadmin/subscriptions`, { headers }),
        axios.get(`${API}/api/superadmin/subscriptions/history`, { headers }),
      ]);
      setPharmacies(subRes.data);
      setHistory(histRes.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const filtered = pharmacies.filter(p =>
    (p.pharmacy_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const planInfo = (id) => PLANS.find(p => p.id === id) || PLANS[0];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="subscription-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Crown className="w-6 h-6" style={{ color: "#FFC300" }} />
              Gestion des Abonnements
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Attribuez des formules, g&eacute;rez les overrides et l'historique
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-white/10 hover:bg-white/5 text-slate-600"
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4" />
              Historique
            </button>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64 bg-white"
                data-testid="search-pharmacy"
              />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PLANS.map(plan => {
            const count = pharmacies.filter(p => p.active_plan === plan.id).length;
            return (
              <Card key={plan.id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${plan.color}20` }}>
                    <Shield className="w-5 h-5" style={{ color: plan.color }} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">{count}</p>
                    <p className="text-xs text-slate-500">{plan.name}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* History panel */}
        {showHistory && (
          <Card className="border-0 shadow-md" data-testid="history-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-slate-200">Historique des modifications</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucune modification</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.slice(0, 30).map((h, i) => (
                    <div key={h.id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 text-sm">
                      <div>
                        <span className="font-semibold text-slate-200">{h.pharmacy_name || h.user_id?.slice(0, 8)}</span>
                        <span className="text-slate-400 mx-2">&mdash;</span>
                        <span className="text-slate-600">
                          {h.action === "assign_plan" && `Plan ${h.plan?.toUpperCase()} attribu\u00e9`}
                          {h.action === "feature_override" && "Override fonctionnalit\u00e9s"}
                          {h.action === "suspend_subscription" && "Abonnement suspendu"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400">
                        {h.performed_by_name} &middot; {new Date(h.created_at).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pharmacies list */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-2">
            {filtered.map(p => {
              const plan = planInfo(p.active_plan);
              const isExpanded = expandedId === p.user_id;
              const hasOverrides = Object.keys(p.feature_overrides || {}).length > 0;

              return (
                <Card key={p.user_id} className="border-0 shadow-sm overflow-hidden" data-testid={`pharmacy-row-${p.user_id}`}>
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : p.user_id)}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold" style={{ background: `${plan.color}20`, color: plan.color }}>
                      {(p.pharmacy_name || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">{p.pharmacy_name || p.email}</p>
                      <p className="text-xs text-slate-400">{p.email} &middot; {p.commune}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {hasOverrides && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-400">OVERRIDE</span>
                      )}
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${plan.color}20`, color: plan.color }}>
                        {plan.name}
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase">{p.subscription_source}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-white/5 bg-white/5/50">
                      <div className="grid md:grid-cols-3 gap-3 mt-3">
                        {/* Assign plan */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setAssignModal(p); }}
                          className="flex items-center gap-2 p-3 rounded-xl border border-white/10 bg-white hover:border-cyan-300 transition-all text-left"
                          data-testid={`assign-plan-${p.user_id}`}
                        >
                          <Crown className="w-5 h-5" style={{ color: "#00B4D8" }} />
                          <div>
                            <p className="text-sm font-semibold text-white">Attribuer une formule</p>
                            <p className="text-xs text-slate-400">Abonnement manuel</p>
                          </div>
                        </button>

                        {/* Feature overrides */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setOverrideModal(p); }}
                          className="flex items-center gap-2 p-3 rounded-xl border border-white/10 bg-white hover:border-amber-300 transition-all text-left"
                          data-testid={`override-features-${p.user_id}`}
                        >
                          <ToggleRight className="w-5 h-5" style={{ color: "#FFC300" }} />
                          <div>
                            <p className="text-sm font-semibold text-white">Override fonctionnalit&eacute;s</p>
                            <p className="text-xs text-slate-400">Activer/d&eacute;sactiver</p>
                          </div>
                        </button>

                        {/* Suspend */}
                        {p.subscription_source === "manuel" && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm("Suspendre l'abonnement ?")) return;
                              try {
                                await axios.post(`${API}/api/superadmin/subscriptions/${p.user_id}/suspend`, {}, { headers });
                                toast.success("Abonnement suspendu");
                                loadData();
                              } catch { toast.error("Erreur"); }
                            }}
                            className="flex items-center gap-2 p-3 rounded-xl border border-red-500/20 bg-white hover:border-red-400 transition-all text-left"
                            data-testid={`suspend-sub-${p.user_id}`}
                          >
                            <X className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="text-sm font-semibold text-red-400">Suspendre</p>
                              <p className="text-xs text-slate-400">Abonnement manuel</p>
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Current overrides display */}
                      {hasOverrides && (
                        <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold text-amber-400">Overrides actifs</p>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/api/superadmin/subscriptions/${p.user_id}/feature-override`, { headers });
                                  toast.success("Overrides supprim\u00e9s");
                                  loadData();
                                } catch { toast.error("Erreur"); }
                              }}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Supprimer tous
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(p.feature_overrides).map(([k, v]) => (
                              <span key={k} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${v ? "bg-green-100 text-green-400" : "bg-red-100 text-red-400"}`}>
                                {v ? "+" : "-"} {FEATURE_LABELS[k] || k}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Assign Plan Modal */}
        {assignModal && (
          <AssignPlanModal
            pharmacy={assignModal}
            headers={headers}
            onClose={() => setAssignModal(null)}
            onDone={() => { setAssignModal(null); loadData(); }}
          />
        )}

        {/* Override Features Modal */}
        {overrideModal && (
          <OverrideFeaturesModal
            pharmacy={overrideModal}
            headers={headers}
            onClose={() => setOverrideModal(null)}
            onDone={() => { setOverrideModal(null); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
}


function AssignPlanModal({ pharmacy, headers, onClose, onDone }) {
  const [plan, setPlan] = useState(pharmacy.active_plan || "decouverte");
  const [paymentMethod, setPaymentMethod] = useState("espèces");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/superadmin/subscriptions/${pharmacy.user_id}/assign-plan`, {
        plan, payment_method: paymentMethod, end_date: endDate, notes,
      }, { headers });
      toast.success(`Plan ${plan} attribu\u00e9 !`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md shadow-2xl border-0" onClick={e => e.stopPropagation()} data-testid="assign-plan-modal">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Attribuer une formule</CardTitle>
            <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <p className="text-sm text-slate-500">{pharmacy.pharmacy_name || pharmacy.email}</p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Formule</Label>
            <div className="grid grid-cols-2 gap-2">
              {PLANS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlan(p.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${plan === p.id ? "border-current shadow-md" : "border-white/10"}`}
                  style={plan === p.id ? { borderColor: p.color, background: `${p.color}10` } : {}}
                >
                  <p className="text-sm font-bold" style={{ color: p.color }}>{p.name}</p>
                  <p className="text-[10px] text-slate-500">{p.price}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Mode de paiement</Label>
            <select
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
              className="w-full mt-1 p-2 rounded-lg border border-white/10 bg-white text-sm text-white"
            >
              <option value="espèces">Esp&egrave;ces</option>
              <option value="virement">Virement bancaire</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="cheque">Ch&egrave;que</option>
              <option value="gratuit">Gratuit / Partenariat</option>
              <option value="autre">Autre</option>
            </select>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Date de fin (optionnel)</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-slate-900" />
          </div>

          <div>
            <Label className="text-xs text-slate-600">Notes</Label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Ex: Paiement re\u00e7u le 15/03, re\u00e7u N\u00b0123..."
              className="w-full mt-1 p-2 rounded-lg border border-white/10 bg-white text-sm text-white resize-none"
              rows={2}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 font-semibold"
            style={{ background: "#00B4D8", color: "#fff" }}
            data-testid="confirm-assign-plan"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Attribuer le plan</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


function OverrideFeaturesModal({ pharmacy, headers, onClose, onDone }) {
  const [overrides, setOverrides] = useState({ ...pharmacy.feature_overrides });
  const [loading, setLoading] = useState(false);

  const toggleFeature = (key) => {
    const newOverrides = { ...overrides };
    if (key in newOverrides) {
      delete newOverrides[key];
    } else {
      newOverrides[key] = true;
    }
    setOverrides(newOverrides);
  };

  const setFeatureValue = (key, value) => {
    setOverrides({ ...overrides, [key]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/superadmin/subscriptions/${pharmacy.user_id}/feature-override`, {
        overrides,
      }, { headers });
      toast.success("Fonctionnalit\u00e9s mises \u00e0 jour !");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md shadow-2xl border-0 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="override-features-modal">
        <CardHeader className="pb-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Override fonctionnalit&eacute;s</CardTitle>
            <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <p className="text-sm text-slate-500">{pharmacy.pharmacy_name} &middot; Plan: {pharmacy.active_plan}</p>
        </CardHeader>
        <CardContent className="pt-4 space-y-1 overflow-y-auto flex-1">
          <p className="text-xs text-slate-400 mb-3">
            Activez/d&eacute;sactivez des fonctionnalit&eacute;s individuellement (au-del&agrave; du plan).
            Les overrides sont marqu&eacute;s en couleur.
          </p>
          {Object.entries(FEATURE_LABELS).map(([key, label]) => {
            const hasOverride = key in overrides;
            const value = hasOverride ? overrides[key] : null;

            return (
              <div key={key} className={`flex items-center justify-between p-2.5 rounded-lg ${hasOverride ? (value ? "bg-green-500/10" : "bg-red-500/10") : "hover:bg-white/5"}`}>
                <span className="text-sm text-slate-200">{label}</span>
                <div className="flex items-center gap-2">
                  {hasOverride && (
                    <button
                      onClick={() => toggleFeature(key)}
                      className="text-[10px] text-slate-400 hover:text-red-500"
                      title="Supprimer l'override"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setFeatureValue(key, !value)}
                    className="flex items-center"
                  >
                    {value ? (
                      <ToggleRight className="w-7 h-7 text-green-500" />
                    ) : hasOverride ? (
                      <ToggleLeft className="w-7 h-7 text-red-400" />
                    ) : (
                      <ToggleLeft className="w-7 h-7 text-slate-300" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </CardContent>
        <div className="p-4 border-t flex-shrink-0">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full py-4 font-semibold"
            style={{ background: "#FFC300", color: "#0f172a" }}
            data-testid="save-overrides-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer les overrides</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}
